import {fontStyles, theme} from '@styles/theme';
import styled from 'styled-components';
import {StyledSelectProps} from './Select';

export const SelectWrapper = styled.div<StyledSelectProps>`
  min-width: 0;
  display: flex;
  width: 100%;
  height: 46px;
  border-radius: ${theme.roundedMedium};
  cursor: ${({inputDisabled}) => (inputDisabled ? 'default' : 'pointer')};
  position: relative;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  border: ${({borderColor, focus, error, inputDisabled}: StyledSelectProps) => {
    switch (true) {
      case borderColor && borderColor.length !== 0 && error && error!.length !== 0:
        return `1px solid ${theme?.colors.signalRed00}`;
      case borderColor && borderColor.length !== 0:
        return `1px solid ${borderColor}`;
      case inputDisabled:
        return `1px solid ${theme?.colors.basic05}`;
      case focus:
        return `1px solid ${theme?.colors.basic03}`;
      case error && error.length !== 0:
        return `1px solid ${theme?.colors.signalRed00}`;
      default:
        return `1px solid ${theme?.colors.basic05}`;
    }
  }};
  background-color: ${theme.colors.basic08};
  transition: all 0.5s;
  & label {
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: calc(100% - 16px);
  }
`;

export const Label = styled.label<StyledSelectProps>`
  ${({shrink}) => (shrink ? fontStyles.tinyR : fontStyles.bodyNum)}
  color: ${theme.colors.basic03};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: calc(100% - 21px);
  transition: 0.2s ease-in-out;
  position: absolute;
  left: 12px;
  ${({shrink}) => (shrink ? 'top: 6px;' : 'top: 14px;')}
`;

export const SelectItem = styled.div<StyledSelectProps>`
  ${fontStyles.bodyNum}
  background-color: transparent;
  border: none;
  padding: 24px 10px 8px 12px;
  outline: none;
  width: calc(100% - 40px);
  white-space: ${({wrap}) => (wrap ? 'wrap' : 'nowrap')};
  overflow: hidden;
  text-overflow: ellipsis;
  &:after {
    width: ${({withSearching}) => (withSearching ? '16px' : '8px')};
    height: ${({withSearching}) => (withSearching ? '16px' : '5px')};
    content: '';
    display: inline-block;
    background-image: url(${({withSearching}) =>
      withSearching ? "'/assets/icons/search.svg'" : "'/assets/icons/caretDown.svg'"});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    position: absolute;
    top: ${({withSearching}) => (withSearching ? '16px' : '22px')};
    right: 16px;
  }
`;

export const SelectList = styled.div<{
  isOpen: boolean;
  position: 'top' | 'bottom';
  menuWidth: string;
}>`
  position: absolute;
  ${({position}) => (position === 'top' ? 'top: 100%;' : 'bottom: 100%;')}
  left: -1px;
  background-color: white;
  border: 1px solid ${theme.colors.basic03};
  height: fit-content;
  min-height: 30px;
  max-height: 200px;
  width: ${({menuWidth}) => menuWidth};
  outline: none;
  border-radius: ${theme.roundedMedium};
  opacity: ${({isOpen}) => (isOpen ? '1' : '0')};
  display: ${({isOpen}) => (isOpen ? '' : 'none')};
  z-index: ${({isOpen}) => (isOpen ? '999999' : '-1')};
  overflow-y: scroll;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  overflow-block: scroll;
  &::-webkit-scrollbar {
    height: 6px;
    width: 0px;
    background: #ededed;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    cursor: pointer;
    width: 4px;
    background: #b1b1b1;
    border-radius: 4px;
    &:hover {
      background-color: ${theme.colors.basic00};
    }
  }
`;

export const Option = styled.div<{
  choosed: boolean;
  disabled: boolean;
  highlighted: boolean;
}>`
  position: relative;
  display: ${({disabled}) => (disabled ? 'none' : 'flex')};
  gap: 8px;
  align-items: center;
  word-wrap: break-word;
  pointer-events: ${({disabled}) => (disabled ? 'none' : '')};
  background-color: ${({highlighted}) =>
    highlighted ? `${theme.colors.accent02}` : `${theme.colors.basic08}`};
  padding: 12px 16px;
  &:hover {
    cursor: pointer;
  }
`;

export const OptionTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SearchingInput = styled.input<StyledSelectProps>`
  ${fontStyles.bodyNum}
  background-color: transparent;
  border: none;
  padding: 24px 10px 8px 12px;
  outline: none;
  width: calc(100% - 40px);
  white-space: ${({wrap}) => (wrap ? 'wrap' : 'nowrap')};
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${theme.colors.basic00};
  }
`;

export const SearchingWrapper = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  align-items: center;
  padding-right: 10px;
  position: relative;
  &:after {
    width: 16px;
    height: 16px;
    content: '';
    display: inline-block;
    background-image: url('/assets/icons/search.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    position: absolute;
    top: 16px;
    right: 16px;
  }
`;

export const ErrorText = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.1px;
  color: ${theme.colors.signalRed00};
  margin-top: 12px;
  overflow-wrap: break-word;
`;
